import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * A draggable divider between two flex panes that share one box.
 *
 * The other splitter in this app (`useSplitter`) divides a three-column CSS
 * grid and only ever drags sideways. This one is for a pair of panes inside a
 * single flex container that may be stacked OR side by side depending on how
 * much room there is - the co-pilot panel is exactly that: transcript over
 * suggestions on a card, transcript beside suggestions once the window fills
 * the screen. So the axis is not a setting, it is read off the box.
 *
 * THE HOOK NEVER MEASURES THE PANES, only the box. The two of them share one
 * fraction - the first gets `var(--crm-cp-split)` of the main axis and the
 * second gets the rest - so the boundary is `fraction * boxSize` by
 * construction. Measuring a pane instead would be reading back the number we
 * just wrote, and would go wrong the moment a pane refused to shrink.
 *
 * The contract with the stylesheet is two lines and both have to be there:
 *
 *   .first  { flex: 1 1 var(--crm-cp-split, 55%); }
 *   .second { flex: 1 1 calc(100% - var(--crm-cp-split, 55%)); }
 *
 * ⚠ Neither pane may carry a `flex-1` / `shrink-0` utility. Those land in the
 * utilities layer, which comes after `@layer components`, so they would replace
 * the basis above with `0%` and the split would silently stop existing.
 */
export interface FlexSplitOptions {
  /** Smallest either pane may be squeezed to, in pixels. */
  minPane: number;
  /** Where the boundary sits before anybody has dragged it, 0-1. */
  initial?: number;
  /**
   * localStorage key to remember the choice under; the axis is appended, so a
   * split dragged while stacked does not decide the side-by-side one. Omit and
   * the split lasts only as long as the panel is open.
   */
  storageKey?: string;
}

type Axis = "x" | "y";

/** The custom property both panes read. Written on the box, never on a pane. */
const SPLIT_VAR = "--crm-cp-split";

function readStored(key: string | undefined, axis: Axis): number | null {
  if (!key) return null;
  try {
    const raw = Number(localStorage.getItem(`${key}.${axis}`));
    // A stored 0 or 1 would be a pane with nothing in it; treat it as absent
    // rather than as a choice, whatever put it there.
    return raw > 0.05 && raw < 0.95 ? raw : null;
  } catch {
    // Private mode, or storage switched off. Nothing was ever chosen here.
    return null;
  }
}

function writeStored(key: string | undefined, axis: Axis, value: number | null): void {
  if (!key) return;
  try {
    if (value === null) localStorage.removeItem(`${key}.${axis}`);
    else localStorage.setItem(`${key}.${axis}`, String(Math.round(value * 1000) / 1000));
  } catch {
    // Not being able to remember the choice is no reason to refuse it.
  }
}

export function useFlexSplit({ minPane, initial = 0.55, storageKey }: FlexSplitOptions) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<HTMLButtonElement | null>(null);
  /** The live fraction, per axis. A ref, not state: a drag writes the CSS
   *  variable and the handle's own offset straight to the DOM, and a render per
   *  pointer move would put React in the middle of a gesture for nothing. */
  const splitRef = useRef<Record<Axis, number | null>>({ x: null, y: null });
  const dragRef = useRef<{ at: number; size: number; start: number } | null>(null);
  /**
   * Which way the panes are laid out. State rather than a ref, because the
   * handle's own shape, cursor and `aria-orientation` all follow it - and it
   * changes when the window goes fullscreen, not while anybody is dragging.
   */
  const [axis, setAxis] = useState<Axis>("y");

  /** Both ends of one drag, in pixels along the current axis. */
  const bounds = useCallback(
    (size: number) => {
      if (size < minPane * 2) return { lo: size / 2, hi: size / 2 };
      return { lo: minPane, hi: size - minPane };
    },
    [minPane],
  );

  /**
   * Push the current fraction into the box and put the handle on the boundary.
   *
   * Runs on every layout change rather than only after a drag: the panel is
   * inside a window that morphs between three widths, and a handle placed once
   * would sit in the middle of a pane for the length of the animation.
   */
  const apply = useCallback(() => {
    const box = boxRef.current;
    const handle = handleRef.current;
    if (!box || !handle) return;

    const next: Axis = getComputedStyle(box).flexDirection.startsWith("row") ? "x" : "y";
    setAxis((current) => (current === next ? current : next));

    const rect = box.getBoundingClientRect();
    const size = next === "x" ? rect.width : rect.height;
    if (size <= 0) return;

    const stored = splitRef.current[next] ?? readStored(storageKey, next);
    splitRef.current[next] = stored;

    if (stored === null) box.style.removeProperty(SPLIT_VAR);
    else box.style.setProperty(SPLIT_VAR, `${(stored * 100).toFixed(3)}%`);

    const fraction = stored ?? initial;
    const at = Math.round(fraction * size);
    // The unused offset is REMOVED rather than zeroed: the stylesheet pins the
    // handle to both edges of the cross axis, and an inline `left: 0` would beat
    // the `right: 0` it is paired with and collapse the strip to nothing.
    if (next === "x") {
      handle.style.left = `${at}px`;
      handle.style.removeProperty("top");
    } else {
      handle.style.top = `${at}px`;
      handle.style.removeProperty("left");
    }
    handle.setAttribute("aria-valuenow", String(Math.round(fraction * 100)));
  }, [initial, storageKey]);

  useLayoutEffect(() => {
    apply();
  }, [apply]);

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const ro = new ResizeObserver(apply);
    ro.observe(box);
    return () => ro.disconnect();
  }, [apply]);

  const setAt = useCallback(
    (at: number) => {
      const box = boxRef.current;
      if (!box) return;
      const rect = box.getBoundingClientRect();
      const size = axis === "x" ? rect.width : rect.height;
      if (size <= 0) return;
      const { lo, hi } = bounds(size);
      const fraction = Math.min(hi, Math.max(lo, at)) / size;
      splitRef.current[axis] = fraction;
      writeStored(storageKey, axis, fraction);
      apply();
    },
    [apply, axis, bounds, storageKey],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      const box = boxRef.current;
      const handle = handleRef.current;
      if (!box || !handle) return;
      e.preventDefault();
      const rect = box.getBoundingClientRect();
      dragRef.current = {
        at: axis === "x" ? e.clientX : e.clientY,
        size: axis === "x" ? rect.width : rect.height,
        start: axis === "x" ? rect.left : rect.top,
      };
      handle.setPointerCapture(e.pointerId);
      handle.classList.add("is-on");
      document.body.classList.add(axis === "x" ? "crm-colresize" : "crm-rowresize");
    },
    [axis],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      const drag = dragRef.current;
      if (!drag) return;
      // Measured against the box's own edge rather than accumulated from the
      // pointer's first position: the box can move mid-drag (the window is a
      // spring), and a delta would carry that displacement into the split.
      setAt((axis === "x" ? e.clientX : e.clientY) - drag.start);
    },
    [axis, setAt],
  );

  const endDrag = useCallback(() => {
    dragRef.current = null;
    handleRef.current?.classList.remove("is-on");
    document.body.classList.remove("crm-colresize", "crm-rowresize");
  }, []);

  /** Back to the proportions the panel ships with, the way a window divider resets. */
  const onDoubleClick = useCallback(() => {
    splitRef.current[axis] = null;
    writeStored(storageKey, axis, null);
    apply();
  }, [apply, axis, storageKey]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      const back = axis === "x" ? "ArrowLeft" : "ArrowUp";
      const forth = axis === "x" ? "ArrowRight" : "ArrowDown";
      if (e.key !== back && e.key !== forth) return;
      const box = boxRef.current;
      if (!box) return;
      e.preventDefault();
      const rect = box.getBoundingClientRect();
      const size = axis === "x" ? rect.width : rect.height;
      const step = (e.shiftKey ? 48 : 16) * (e.key === forth ? 1 : -1);
      setAt((splitRef.current[axis] ?? initial) * size + step);
    },
    [axis, initial, setAt],
  );

  return {
    boxRef,
    axis,
    handleProps: {
      ref: handleRef,
      type: "button" as const,
      className: "crm-cp-split",
      "data-axis": axis,
      role: "separator",
      "aria-orientation": (axis === "x" ? "vertical" : "horizontal") as "vertical" | "horizontal",
      "aria-valuemin": 0,
      "aria-valuemax": 100,
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onDoubleClick,
      onKeyDown,
    },
  };
}
